import { useState, useEffect, useCallback } from "react";
import { useAuth, API_URL } from "../../context/AuthContext";
import ProductSection from "./ProductSection";
import PriceSection from "./PriceSection";
import SpecsSection from "./SpecsSection";
import OptionsSection from "./OptionsSection";
import ImagesSection from "./ImagesSection";
import CategoriesSection from "./CategoriesSection";
import NewProductDialog from "./NewProductDialog";
import PasswordDialog from "./PasswordDialog";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
  Divider,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import LockIcon from "@mui/icons-material/Lock";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import CategoryIcon from "@mui/icons-material/Category";

export interface ProductData {
  id: number;
  slug: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  title_en: string | null;
  subtitle_en: string | null;
  category_id: number | null;
  hero_image_id: number | null;
  prices: Array<{ id: number; label: string; label_en?: string; amount: number }>;
  specs: Array<{ id: number; text: string; text_en?: string }>;
  options: Array<{ id: number; name: string; name_en?: string; amount: number }>;
  images: Array<{ id: number; filename: string; url: string; sort_order: number }>;
}

export type CategoryData = Array<{ id: number; name: string; name_en?: string; sort_order: number }>;

const sectionLabels = ["Dane produktu", "Ceny", "Specyfikacja", "Opcje dodatkowe", "Zdjęcia"];

export default function Dashboard() {
  const [products, setProducts] = useState<ProductData[]>([]);
  const [categories, setCategories] = useState<CategoryData>([]);
  const [loading, setLoading] = useState(true);
  const [activeProductId, setActiveProductId] = useState<number | null>(null);
  const [activeSection, setActiveSection] = useState(0);
  const [showCategories, setShowCategories] = useState(false);
  const [search, setSearch] = useState("");
  const [pwOpen, setPwOpen] = useState(false);
  const [newProductOpen, setNewProductOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { token, username, logout } = useAuth();

  const fetchProducts = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setProducts(data);
    } catch {
      setError("Nie udało się załadować danych. Sprawdź połączenie z serwerem.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setCategories(data);
    } catch {
      setCategories([]);
    }
  }, [token]);

  useEffect(() => { fetchProducts(); fetchCategories(); }, [fetchProducts, fetchCategories]);

  const refreshProducts = useCallback(async () => {
    await fetchProducts();
    setRefreshKey((k) => k + 1);
  }, [fetchProducts]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: "80vh", backgroundColor: "#1a1a1a", color: "#f0f0f0" }}>
        <Box maxWidth="md" sx={{ py: 4, px: 2 }}>
          <Typography variant="h5" sx={{ color: "#f44336" }}>{error}</Typography>
          <Button onClick={() => { setError(null); setLoading(true); fetchProducts(); }} sx={{ mt: 2, color: "#1976d2" }}>
            Spróbuj ponownie
          </Button>
          <Button onClick={logout} sx={{ mt: 2, ml: 2, color: "#aaa" }}>
            Wyloguj
          </Button>
        </Box>
      </Box>
    );
  }

  const filteredProducts = products.filter((p) =>
    p.title.toLowerCase().includes(search.toLowerCase()) ||
    p.slug.toLowerCase().includes(search.toLowerCase())
  );

  const product = products.find((p) => p.id === activeProductId);

  const handleSelectProduct = (id: number) => {
    setActiveProductId(id);
    setShowCategories(false);
    setActiveSection(0);
  };

  const handleSelectCategories = () => {
    setShowCategories(true);
    setActiveProductId(null);
    setActiveSection(0);
  };

  const handleProductDeleted = async () => {
    await fetchProducts();
    setActiveProductId(null);
    setActiveSection(0);
  };

  const handleProductCreated = async () => {
    await fetchProducts();
    setShowCategories(false);
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#1a1a1a", color: "#f0f0f0" }}>
      {/* Header */}
      <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #333" }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Panel administracyjny</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="body2" sx={{ color: "#aaa" }}>{username}</Typography>
          <Button size="small" startIcon={<LockIcon />} onClick={() => setPwOpen(true)} sx={{ color: "#aaa" }}>Hasło</Button>
          <Button size="small" startIcon={<LogoutIcon />} onClick={logout} sx={{ color: "#f44336" }}>Wyloguj</Button>
        </Box>
      </Box>

      <Box sx={{ display: "flex", height: "calc(100vh - 57px)" }}>
        {/* Sidebar */}
        <Paper sx={{ width: 280, minWidth: 280, borderRadius: 0, borderRight: "1px solid #333", backgroundColor: "#222", display: "flex", flexDirection: "column" }}>
          {/* Search */}
          <Box sx={{ p: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Szukaj produktu..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#666", fontSize: 20 }} />
                  </InputAdornment>
                ),
                sx: { color: "#f0f0f0", fontSize: "0.875rem" },
              }}
            />
          </Box>

          {/* Product list */}
          <Box sx={{ flex: 1, overflow: "auto" }}>
            {filteredProducts.map((p) => (
              <Box
                key={p.id}
                onClick={() => handleSelectProduct(p.id)}
                sx={{
                  px: 2,
                  py: 1.5,
                  cursor: "pointer",
                  borderLeft: "3px solid",
                  borderColor: activeProductId === p.id && !showCategories ? "#1976d2" : "transparent",
                  backgroundColor: activeProductId === p.id && !showCategories ? "#2a2a2a" : "transparent",
                  "&:hover": { backgroundColor: "#2a2a2a" },
                  transition: "background-color 0.15s",
                }}
              >
                <Typography sx={{ color: "#f0f0f0", fontSize: "0.875rem", fontWeight: activeProductId === p.id && !showCategories ? 700 : 400 }}>
                  {p.title}
                </Typography>
                <Typography sx={{ color: "#666", fontSize: "0.75rem" }}>
                  /{p.slug}
                </Typography>
              </Box>
            ))}
            {filteredProducts.length === 0 && (
              <Typography sx={{ px: 2, py: 1, color: "#666", fontSize: "0.85rem" }}>
                {search ? "Brak wyników" : "Brak produktów"}
              </Typography>
            )}
          </Box>

          {/* Bottom actions */}
          <Divider sx={{ borderColor: "#333" }} />
          <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
            <Button
              fullWidth
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setNewProductOpen(true)}
              sx={{ color: "#4caf50", justifyContent: "flex-start" }}
            >
              Nowy produkt
            </Button>
            <Button
              fullWidth
              size="small"
              startIcon={<CategoryIcon />}
              onClick={handleSelectCategories}
              sx={{
                color: showCategories ? "#ff9800" : "#aaa",
                justifyContent: "flex-start",
                fontWeight: showCategories ? 700 : 400,
                backgroundColor: showCategories ? "#2a2a2a" : "transparent",
              }}
            >
              Kategorie
            </Button>
          </Box>
        </Paper>

        {/* Main content */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          {showCategories ? (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ color: "#ff9800", mb: 2 }}>Zarządzanie kategoriami</Typography>
              <CategoriesSection categories={categories} onChange={fetchCategories} />
            </Box>
          ) : product ? (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Inventory2Icon sx={{ color: "#1976d2" }} />
                  <Typography variant="h6" sx={{ color: "#1976d2" }}>{product.title}</Typography>
                </Box>
              </Box>

              {/* Section tabs */}
              <Box sx={{ display: "flex", gap: 0.5, mb: 3 }}>
                {sectionLabels.map((label, i) => (
                  <Button
                    key={i}
                    size="small"
                    onClick={() => setActiveSection(i)}
                    sx={{
                      color: activeSection === i ? "#1976d2" : "#aaa",
                      fontWeight: activeSection === i ? 700 : 400,
                      borderBottom: activeSection === i ? "2px solid #1976d2" : "2px solid transparent",
                      borderRadius: 0,
                      px: 2,
                      "&:hover": { backgroundColor: "transparent", color: "#f0f0f0" },
                    }}
                  >
                    {label}
                  </Button>
                ))}
              </Box>

              {activeSection === 0 && <ProductSection product={product} token={token} onChange={() => { refreshProducts(); fetchCategories(); }} onDelete={handleProductDeleted} categories={categories} />}
              {activeSection === 1 && <PriceSection key={refreshKey} product={product} token={token} onChange={refreshProducts} />}
              {activeSection === 2 && <SpecsSection key={refreshKey} product={product} token={token} onChange={refreshProducts} />}
              {activeSection === 3 && <OptionsSection key={refreshKey} product={product} token={token} onChange={refreshProducts} />}
              {activeSection === 4 && <ImagesSection product={product} token={token} onChange={fetchProducts} />}
            </Box>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", color: "#555" }}>
              <Typography>Wybierz produkt z listy po lewej</Typography>
            </Box>
          )}
        </Box>
      </Box>

      <PasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} token={token} />
      <NewProductDialog open={newProductOpen} onClose={() => setNewProductOpen(false)} token={token} onCreated={handleProductCreated} categories={categories} />
    </Box>
  );
}
